// CreateChooserModal — the shared "how do you want to create this?" fork, used
// by both course creation and need (annons) creation so the two entry points
// stay identical. Two doors:
//   • Manual — build it yourself in the form/canvas (icon = the entity glyph).
//   • Link   — describe it in a chat and let the global Link assistant build it.
//
// The doors are behaviour-agnostic: callers pass the onClick handlers (manual →
// open the form; link → stage the global Link with a create prompt). This keeps
// the modal a pure presentational fork.
import React from 'react';
import { X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ModalPortal } from '@/components/custom-ui/ModalPortal';
import { IconChip } from '@/components/custom-ui/IconChip';
import { LinkAvatar } from '@/components/icons/LinkAvatar';

interface DoorProps {
    title: string;
    description: string;
    onClick: () => void;
    children: React.ReactNode; // the leading visual (IconChip / LinkAvatar)
}

const Door: React.FC<DoorProps> = ({ title, description, onClick, children }) => (
    <button
        onClick={onClick}
        className="flex flex-col items-start gap-3 p-5 border-2 border-gray-200 hover:border-[#1A4D4F] hover:bg-[#A4C3B2]/20 rounded-2xl transition-all text-left h-full"
    >
        {children}
        <div>
            <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
        </div>
    </button>
);

interface CreateChooserModalProps {
    title: string;
    /** Defaults to "Hur vill du skapa?" */
    question?: string;
    manual: { icon: LucideIcon; title: string; description: string; onClick: () => void };
    link: { title: string; description: string; onClick: () => void };
    onClose: () => void;
}

const CreateChooserModal: React.FC<CreateChooserModalProps> = ({ title, question = 'Hur vill du skapa?', manual, link, onClose }) => {
    const ManualIcon = manual.icon;
    return (
    <ModalPortal>
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Stäng">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Two doors */}
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">{question}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Door title={manual.title} description={manual.description} onClick={manual.onClick}>
                            <IconChip icon={manual.icon} size="lg" tone="brand" />
                        </Door>
                        <Door title={link.title} description={link.description} onClick={link.onClick}>
                            {/* Link mark with the entity glyph as a corner badge —
                                same "whose/what" overlay pattern as EntityAvatar. */}
                            <span className="relative inline-block flex-shrink-0 w-12 h-12">
                                <LinkAvatar className="w-12 h-12" />
                                <span className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm text-[#1A4D4F]">
                                    <ManualIcon className="w-3.5 h-3.5" />
                                </span>
                            </span>
                        </Door>
                    </div>
                </div>
            </div>
        </div>
    </ModalPortal>
    );
};

export default CreateChooserModal;
